import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2, Pencil, Archive } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { canManageJournalTemplate } from '@/utils/journalTemplates';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import {
  useJournalTemplates,
  useCreateJournalTemplate,
  useUpdateJournalTemplate,
  useArchiveJournalTemplate,
  type JournalTemplateRow,
} from '@/hooks/useJournalTemplates';

export function JournalTemplates() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isMainClinicAdmin, isPlatformAdmin } = useAuth();
  const isAdmin = isMainClinicAdmin || isPlatformAdmin;

  const [showCreate, setShowCreate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<JournalTemplateRow | null>(null);

  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formScope, setFormScope] = useState<'personal' | 'clinic'>('clinic');

  const templatesQuery = useJournalTemplates(activeClinicId);
  const createTemplate = useCreateJournalTemplate(activeClinicId);
  const updateTemplate = useUpdateJournalTemplate(activeClinicId);
  const archiveTemplate = useArchiveJournalTemplate(activeClinicId);

  const templates = templatesQuery.data ?? [];

  const openCreate = () => {
    setFormTitle('');
    setFormContent('');
    setFormDescription('');
    setFormScope(isAdmin ? 'clinic' : 'personal');
    setShowCreate(true);
  };

  const openEdit = (tmpl: JournalTemplateRow) => {
    setFormTitle(tmpl.title);
    setFormContent(tmpl.content_md);
    setFormDescription(tmpl.description ?? '');
    setFormScope(tmpl.scope === 'personal' ? 'personal' : 'clinic');
    setEditingTemplate(tmpl);
  };

  const handleCreate = async () => {
    if (!formTitle.trim()) return;
    await createTemplate.mutateAsync({
      title: formTitle.trim(),
      content_md: formContent,
      description: formDescription || undefined,
      scope: isAdmin ? formScope : 'personal',
    });
    toast.success(t('journal.template_created'));
    setShowCreate(false);
  };

  const handleUpdate = async () => {
    if (!editingTemplate || !formTitle.trim()) return;
    await updateTemplate.mutateAsync({
      id: editingTemplate.id,
      title: formTitle.trim(),
      content_md: formContent,
      description: formDescription || undefined,
      scope: isAdmin ? formScope : 'personal',
    });
    toast.success(t('journal.template_updated'));
    setEditingTemplate(null);
  };

  const handleArchive = async (tmpl: JournalTemplateRow) => {
    await archiveTemplate.mutateAsync(tmpl.id);
    toast.success(t('journal.template_archived'));
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ds-text">
            {t('journal.templates_title')}
          </h2>
          <p className="mt-1 text-sm text-ds-muted">
            {t('journal.templates_subtitle')}
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('journal.new_template')}
        </Button>
      </div>

      {templatesQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ds-line p-10 text-center">
          <p className="text-sm text-ds-muted">{t('journal.no_templates')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((tmpl) => (
            <div
              key={tmpl.id}
              className="flex items-start gap-3 rounded-xl border border-ds-line bg-ds-card p-4 shadow-card"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ds-text">{tmpl.title}</p>
                {tmpl.description && (
                  <p className="mt-0.5 text-xs text-ds-muted">{tmpl.description}</p>
                )}
                <p className="mt-0.5 text-xs text-ds-muted">
                  {tmpl.scope === 'personal'
                    ? t('journal.template_scope_personal')
                    : t('journal.template_scope_clinic')}
                </p>
                {tmpl.content_md && (
                  <p className="mt-1 line-clamp-2 text-sm text-ds-muted">
                    {tmpl.content_md.replace(/[#*_`]/g, '').slice(0, 100)}
                  </p>
                )}
              </div>
              {canManageJournalTemplate(tmpl, isAdmin) && (
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    title={t('common.edit')}
                    onClick={() => openEdit(tmpl)}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    title={t('journal.archive')}
                    onClick={() => handleArchive(tmpl)}
                  >
                    <Archive />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <Modal title={t('journal.new_template')} onClose={() => setShowCreate(false)}>
          <div className="space-y-3">
            <Input
              label={t('journal.title')}
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder={t('journal.template_title_placeholder')}
            />
            <Input
              label={t('journal.template_description')}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder={t('journal.template_description_placeholder')}
            />
            <Textarea
              label={t('journal.content')}
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
              rows={8}
              placeholder={t('journal.content_placeholder')}
              className="font-mono text-sm"
            />
            {isAdmin && (
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-1.5 text-ds-text">
                  <input
                    type="radio"
                    name="settingsTemplateScopeCreate"
                    checked={formScope === 'personal'}
                    onChange={() => setFormScope('personal')}
                    className="accent-ds-teal"
                  />
                  {t('journal.template_scope_personal')}
                </label>
                <label className="flex items-center gap-1.5 text-ds-text">
                  <input
                    type="radio"
                    name="settingsTemplateScopeCreate"
                    checked={formScope === 'clinic'}
                    onChange={() => setFormScope('clinic')}
                    className="accent-ds-teal"
                  />
                  {t('journal.template_scope_clinic')}
                </label>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!formTitle.trim() || createTemplate.isPending}
              >
                {createTemplate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('journal.create')
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {editingTemplate && (
        <Modal title={t('journal.edit_template')} onClose={() => setEditingTemplate(null)}>
          <div className="space-y-3">
            <Input
              label={t('journal.title')}
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
            />
            <Input
              label={t('journal.template_description')}
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
            />
            <Textarea
              label={t('journal.content')}
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
              rows={8}
              className="font-mono text-sm"
            />
            {isAdmin && (
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-1.5 text-ds-text">
                  <input
                    type="radio"
                    name="settingsTemplateScopeEdit"
                    checked={formScope === 'personal'}
                    onChange={() => setFormScope('personal')}
                    className="accent-ds-teal"
                  />
                  {t('journal.template_scope_personal')}
                </label>
                <label className="flex items-center gap-1.5 text-ds-text">
                  <input
                    type="radio"
                    name="settingsTemplateScopeEdit"
                    checked={formScope === 'clinic'}
                    onChange={() => setFormScope('clinic')}
                    className="accent-ds-teal"
                  />
                  {t('journal.template_scope_clinic')}
                </label>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setEditingTemplate(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={!formTitle.trim() || updateTemplate.isPending}
              >
                {updateTemplate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
